/**
 * Page 3 — Executive Summary (v8 mock parity, refined).
 *
 * - Title moved higher
 * - Larger narrative text + leading
 * - Cyber Resilience meter uses distinct band colors:
 *     Minimal=Red, Adequate=Orange, Strong=Green, Extensive=Blue
 * - Bar sits near the top of its card; client logo above pin
 * - Commentary below the bar pulled from overallSummaryLine() —
 *   the same string rendered in the Cyber Resilience & Functional
 *   Adequacy yellow text box on the Executive Summary (Live) tab.
 */
import type jsPDF from "jspdf";
import { PAGE_MARGIN, REPORT_THEME } from "./theme";
import { type LoadedLogo } from "./loadLogo";
import { drawRunningHeader, drawPageFooter, drawSectionTitle } from "./pageChrome";
import { overallSummaryLine, type MaturityBand } from "@/reports/telecom/cyberResilienceSummary";

export interface ExecutiveSummaryContext {
  narrative?: string | null;
  band: MaturityBand | null;
  clientLogo: LoadedLogo | null;
  clientOrgName: string;
  methodology?: string | null;
  frameworks?: string[] | null;
  pageNumber?: number;
  /**
   * Phase 2.4 — Telecommunications Infrastructure Security Overview block.
   * Appended as a final paragraph above the cyber resilience meter when
   * telecom infrastructure data exists for this assessment. Counts only;
   * no scores, no maturity bands.
   */
  telecomOverview?: string | null;
  /**
   * Phase 2.14 — Vendor Exposure narrative. Only included when vendors exist.
   * CSV Pro does not designate vendors as restricted or prohibited.
   */
  vendorExposureNarrative?: string | null;
  /** Phase 2.14 — Reviewer confirmation flag for vendor exposure section. */
  vendorExposureConfirmed?: boolean;
}

/** Distinct meter colors: Minimal=Red, Adequate=Orange, Strong=Green, Extensive=Blue. */
const METER_COLORS: Record<MaturityBand, { rgb: [number, number, number] }> = {
  Minimal:   { rgb: [220, 38, 38] },   // red-600
  Adequate:  { rgb: [245, 158, 11] },  // amber-500 (orange)
  Strong:    { rgb: [22, 163, 74] },   // green-600
  Extensive: { rgb: [37, 99, 235] },   // blue-600
};

export function renderExecutiveSummary(doc: jsPDF, ctx: ExecutiveSummaryContext): void {
  doc.addPage();
  // pageW/pageH measured below alongside adaptive sizing.


  drawRunningHeader(doc, {
    clientName: ctx.clientOrgName,
    frameworks: ctx.frameworks ?? null,
    methodology: ctx.methodology ?? null,
  });

  let y = drawSectionTitle(doc, {
    sectionNumber: "01",
    title: "Executive Summary",
  });

  // Narrative — tighter top gap so body rises.
  y += 10;
  const narrative =
    (ctx.narrative ?? "").trim() ||
    "The reviewer has not yet authored an executive summary for this assessment.";
  const telecom = (ctx.telecomOverview ?? "").trim();
  const vendorBlock = (ctx.vendorExposureNarrative ?? "").trim();

  type Block = { heading?: string; body: string };
  const blocks: Block[] = [{ body: narrative }];
  if (telecom) blocks.push({ heading: "Telecommunications Infrastructure Security Overview", body: telecom });
  if (vendorBlock) blocks.push({ heading: "Technology & Vendor Exposure", body: vendorBlock });

  // ── Adaptive sizing: protect the Cyber Resilience meter from bleeding
  // into the footer when the narrative is long. We measure required
  // narrative height at progressively smaller font sizes and choose the
  // largest size that still leaves room for the meter card above the footer.
  const pageH = doc.internal.pageSize.getHeight();
  const pageW = doc.internal.pageSize.getWidth();
  const footerReserve = 60; // keep meter clear of "Confidential · Page N"
  const cardPreferredH = 220;
  const cardMinH = 195;
  const gapBeforeCard = 18;
  const narrativeStartY = y;

  type SizeProfile = { fontSize: number; lineH: number; paraGap: number; blockGap: number; headGap: number; subFont: number };
  const PROFILES: SizeProfile[] = [
    { fontSize: 11.5, lineH: 17, paraGap: 22, blockGap: 22, headGap: 10, subFont: 10 },
    { fontSize: 11,   lineH: 16, paraGap: 18, blockGap: 18, headGap: 8,  subFont: 9.5 },
    { fontSize: 10.5, lineH: 15, paraGap: 14, blockGap: 14, headGap: 6,  subFont: 9.5 },
    { fontSize: 10,   lineH: 14, paraGap: 11, blockGap: 12, headGap: 5,  subFont: 9 },
    { fontSize: 9.5,  lineH: 13, paraGap: 9,  blockGap: 10, headGap: 4,  subFont: 9 },
    { fontSize: 9,    lineH: 12, paraGap: 7,  blockGap: 8,  headGap: 3,  subFont: 8.5 },
  ];

  const measureBlocksHeight = (p: SizeProfile): number => {
    doc.setFont("helvetica", "normal");
    doc.setFontSize(p.fontSize);
    let h = 0;
    blocks.forEach((blk, idx) => {
      if (blk.heading) h += p.headGap + 18;
      const paragraphs = blk.body.split(/\n\n+/).map((s) => s.trim()).filter(Boolean);
      paragraphs.forEach((para, pIdx) => {
        const lines = doc.splitTextToSize(para, pageW - PAGE_MARGIN * 2);
        h += lines.length * p.lineH + (pIdx < paragraphs.length - 1 ? p.paraGap : 0);
      });
      if (idx < blocks.length - 1) h += p.blockGap;
    });
    return h;
  };

  const maxNarrativeForPreferred =
    pageH - footerReserve - cardPreferredH - gapBeforeCard - narrativeStartY;

  let chosen: SizeProfile = PROFILES[0];
  for (const p of PROFILES) {
    chosen = p;
    if (measureBlocksHeight(p) <= maxNarrativeForPreferred) break;
  }

  const renderSubheading = (label: string, atY: number, p: SizeProfile): number => {
    doc.setFont("helvetica", "bold");
    doc.setFontSize(p.subFont);
    doc.setTextColor(...REPORT_THEME.navy.rgb);
    doc.text(label.toUpperCase(), PAGE_MARGIN, atY);
    doc.setDrawColor(...REPORT_THEME.gold.rgb);
    doc.setLineWidth(1.1);
    doc.line(PAGE_MARGIN, atY + 4, PAGE_MARGIN + 42, atY + 4);
    return atY + 18;
  };

  blocks.forEach((blk, idx) => {
    if (blk.heading) {
      y += chosen.headGap;
      y = renderSubheading(blk.heading, y, chosen);
    }
    doc.setTextColor(...REPORT_THEME.textPrimary.rgb);
    doc.setFont("helvetica", "normal");
    doc.setFontSize(chosen.fontSize);
    const paragraphs = blk.body.split(/\n\n+/).map((p) => p.trim()).filter(Boolean);
    paragraphs.forEach((p, pIdx) => {
      const lines = doc.splitTextToSize(p, pageW - PAGE_MARGIN * 2);
      doc.text(lines, PAGE_MARGIN, y, { lineHeightFactor: 1.5 } as any);
      y += lines.length * chosen.lineH + (pIdx < paragraphs.length - 1 ? chosen.paraGap : 0);
    });
    if (idx < blocks.length - 1) y += chosen.blockGap;
  });

  // ── Meter card ──
  const cardX = PAGE_MARGIN;
  const cardW = pageW - PAGE_MARGIN * 2;
  let cardTop = y + gapBeforeCard;
  let cardH = cardPreferredH;
  // Hard guard: never bleed into footer.
  const availableForCard = pageH - footerReserve - cardTop;
  if (availableForCard < cardH) cardH = Math.max(cardMinH, availableForCard);
  if (cardTop + cardH > pageH - footerReserve) {
    cardTop = pageH - footerReserve - cardH;
  }

  // Card background + accent frame
  doc.setFillColor(...REPORT_THEME.surfaceAlt.rgb);
  doc.rect(cardX, cardTop, cardW, cardH, "F");
  doc.setDrawColor(...REPORT_THEME.accent.rgb);
  doc.setLineWidth(1.4);
  doc.rect(cardX, cardTop, cardW, cardH, "S");

  drawResilienceMeter(doc, {
    cardX,
    cardW,
    cardTop,
    cardH,
    band: ctx.band,
    clientName: ctx.clientOrgName,
    clientLogo: ctx.clientLogo,
  });

  drawPageFooter(doc, ctx.pageNumber ?? 3);
}

const BAND_ORDER: MaturityBand[] = ["Minimal", "Adequate", "Strong", "Extensive"];

function drawResilienceMeter(
  doc: jsPDF,
  args: {
    cardX: number;
    cardW: number;
    cardTop: number;
    cardH: number;
    band: MaturityBand | null;
    clientName: string;
    clientLogo: LoadedLogo | null;
  },
): void {
  const { cardX, cardW, cardTop, cardH } = args;
  const innerPad = 30;
  const barW = cardW - innerPad * 2;
  const barH = 22;
  const barLeft = cardX + innerPad;
  const titleY = cardTop + 30;
  const barTop = cardTop + 102;
  const segW = barW / BAND_ORDER.length;

  // Centered title at the top of the card: "Cyber Resilience Rating: <Band>".
  const labelPrefix = "Cyber Resilience Rating: ";
  const bandText = args.band ?? "Not Assessed";
  doc.setFont("helvetica", "bold");
  doc.setFontSize(13);
  const prefixW = doc.getTextWidth(labelPrefix);
  const bandW = doc.getTextWidth(bandText);
  const startX = cardX + (cardW - prefixW - bandW) / 2;
  doc.setTextColor(...REPORT_THEME.navy.rgb);
  doc.text(labelPrefix, startX, titleY);
  if (args.band) {
    const c = METER_COLORS[args.band];
    doc.setTextColor(...c.rgb);
  } else {
    doc.setTextColor(...REPORT_THEME.textMuted.rgb);
  }
  doc.text(bandText, startX + prefixW, titleY);
  doc.setDrawColor(...REPORT_THEME.gold.rgb);
  doc.setLineWidth(1.1);
  doc.line(cardX + cardW / 2 - 42, titleY + 9, cardX + cardW / 2 + 42, titleY + 9);

  // Pill segments — rounded ends only on the outer edges
  BAND_ORDER.forEach((band, i) => {
    const c = METER_COLORS[band];
    doc.setFillColor(...c.rgb);
    const x = barLeft + i * segW;
    if (i === 0) {
      doc.roundedRect(x, barTop, segW, barH, barH / 2, barH / 2, "F");
      doc.setFillColor(...c.rgb);
      doc.rect(x + segW / 2, barTop, segW / 2, barH, "F");
    } else if (i === BAND_ORDER.length - 1) {
      doc.roundedRect(x, barTop, segW, barH, barH / 2, barH / 2, "F");
      doc.setFillColor(...c.rgb);
      doc.rect(x, barTop, segW / 2, barH, "F");
    } else {
      doc.rect(x, barTop, segW, barH, "F");
    }
  });

  // White divider lines between segments
  doc.setDrawColor(255, 255, 255);
  doc.setLineWidth(2);
  for (let i = 1; i < BAND_ORDER.length; i++) {
    const x = barLeft + i * segW;
    doc.line(x, barTop + 2, x, barTop + barH - 2);
  }

  // Band labels (colored to match segment)
  doc.setFont("helvetica", "bold");
  doc.setFontSize(10);
  BAND_ORDER.forEach((band, i) => {
    const c = METER_COLORS[band];
    doc.setTextColor(...c.rgb);
    const cx = barLeft + i * segW + segW / 2;
    const tw = doc.getTextWidth(band.toUpperCase());
    doc.text(band.toUpperCase(), cx - tw / 2, barTop + barH + 18);
  });

  // ── Client-logo marker above the active segment ──
  if (args.band) {
    const idx = BAND_ORDER.indexOf(args.band);
    const markerCx = barLeft + idx * segW + segW / 2;
    const bandColor = METER_COLORS[args.band];

    const logoTargetH = 34;
    const logoMaxW = 72;
    const logoBottomGap = 15;

    if (args.clientLogo) {
      let h = logoTargetH;
      let w = (args.clientLogo.width / args.clientLogo.height) * h;
      if (w > logoMaxW) {
        w = logoMaxW;
        h = (args.clientLogo.height / args.clientLogo.width) * w;
      }
      const x = markerCx - w / 2;
      const y = barTop - logoBottomGap - h;
      try {
        doc.addImage(args.clientLogo.dataUrl, args.clientLogo.format, x, y, w, h, undefined, "FAST");
        console.info("[reviewer-pdf] client logo marker: doc.addImage() executes", { x, y, w, h });
      } catch (e) {
        console.warn("[reviewer-pdf] client logo marker: jsPDF addImage failed", e);
        drawInitialsMarker(doc, markerCx, barTop - logoBottomGap - 14, args.clientName);
      }
    } else {
      drawInitialsMarker(doc, markerCx, barTop - logoBottomGap - 14, args.clientName);
    }

    // Small pointer triangle directly beneath logo/initials and above the bar.
    doc.setFillColor(...bandColor.rgb);
    doc.triangle(markerCx - 5, barTop - 9, markerCx + 5, barTop - 9, markerCx, barTop - 2, "F");
  }

  // ── Commentary below bar (sourced from overallSummaryLine — same string
  // rendered in the Cyber Resilience & Functional Adequacy yellow box on
  // the Executive Summary (Live) tab) ──
  const commentaryY = barTop + barH + 44;
  const commentary = overallSummaryLine(args.band ?? "Unrated", args.clientName);
  doc.setTextColor(...REPORT_THEME.textPrimary.rgb);
  doc.setFont("helvetica", "normal");
  doc.setFontSize(10.5);
  const commentaryLines = doc.splitTextToSize(commentary, cardW - innerPad * 2);
  doc.text(commentaryLines, cardX + innerPad, commentaryY, { lineHeightFactor: 1.4 } as any);
}

function drawInitialsMarker(doc: jsPDF, centerX: number, centerY: number, clientName: string): void {
  const initials = (clientName || "Client")
    .split(/\s+/)
    .filter(Boolean)
    .slice(0, 2)
    .map((part) => part.charAt(0).toUpperCase())
    .join("") || "CL";
  const chipR = 14;
  doc.setFillColor(...REPORT_THEME.navy.rgb);
  doc.circle(centerX, centerY, chipR, "F");
  doc.setTextColor(...REPORT_THEME.textOnNavy.rgb);
  doc.setFont("helvetica", "bold");
  doc.setFontSize(initials.length > 1 ? 10 : 12);
  doc.text(initials, centerX - doc.getTextWidth(initials) / 2, centerY + 4);
}

